import {
  infiniteQueryOptions,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { ARCHIVE_PAGE_TYPE } from '@/components/app/archive/helpers';
import {
  getArchiveAdsBanner,
  getArchiveInfo,
  getFilterCategoryList,
  getProductList,
  getSuggestionDemosList,
} from '@/components/app/archive/services';
import {
  ArchivePageType,
  FilterParamsType,
  getAdsBannerType,
  GetArchiveInfoType,
} from '@/components/app/archive/services/type';

import {
  GET_ARCHIVE_ADS_BANNER,
  GET_ARCHIVE_INFO,
  GET_FILTER_CATEGORY_LIST,
  GET_PRODUCT_LIST,
  GET_SUGGESTION_DEMOS_LIST,
} from '@/constant/query-key';

export function useGetArchiveInfo(
  slug: GetArchiveInfoType,
  type: ArchivePageType = ARCHIVE_PAGE_TYPE.CATEGORY,
) {
  return useQuery({
    queryFn: () => getArchiveInfo(slug, type),
    queryKey: [GET_ARCHIVE_INFO, slug, type],
  });
}

export function getArchiveInfoQueryOptions(
  slug: GetArchiveInfoType,
  type: ArchivePageType = ARCHIVE_PAGE_TYPE.CATEGORY,
) {
  return queryOptions({
    queryFn: () => getArchiveInfo(slug, type),
    queryKey: [GET_ARCHIVE_INFO, slug, type],
  });
}

export function useGetProductList(filters: FilterParamsType, page: number) {
  return useQuery({
    queryKey: [GET_PRODUCT_LIST, filters, page],
    queryFn: ({ pageParam = 1 }) => getProductList({ ...filters, page: page }),
  });
}

export function getProductListOptions(filters: FilterParamsType, page: number) {
  return queryOptions({
    queryKey: [GET_PRODUCT_LIST, filters, page],
    queryFn: ({ pageParam = 1 }) => getProductList({ ...filters, page: page }),
  });
}

export function useGetSuggestionDemosList(
  slug: GetArchiveInfoType,
  type: ArchivePageType = ARCHIVE_PAGE_TYPE.CATEGORY,
) {
  return useQuery({
    queryFn: () => getSuggestionDemosList(slug, type),
    queryKey: [GET_SUGGESTION_DEMOS_LIST, slug, type],
  });
}

export function getSuggestionDemosListOptions(
  slug: GetArchiveInfoType,
  type: ArchivePageType = ARCHIVE_PAGE_TYPE.CATEGORY,
) {
  return queryOptions({
    queryFn: () => getSuggestionDemosList(slug, type),
    queryKey: [GET_SUGGESTION_DEMOS_LIST, slug, type],
  });
}

export function useGetInfiniteProductList({
  filters,
  initialPage,
  enabled,
}: {
  filters: FilterParamsType;
  initialPage?: number;
  enabled?: boolean;
}) {
  return useInfiniteQuery({
    initialPageParam: initialPage,
    queryKey: [GET_PRODUCT_LIST, filters],
    queryFn: enabled
      ? ({ pageParam = 2 }) => getProductList({ ...filters, page: pageParam })
      : skipToken,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    getPreviousPageParam: (firstPage) => firstPage.previousCursor,
  });
}

export function getInfiniteProductListOptions(filters: FilterParamsType) {
  return infiniteQueryOptions({
    initialPageParam: 1,
    queryKey: [GET_PRODUCT_LIST, filters],
    queryFn: ({ pageParam = 1 }) => getProductList({ ...filters, page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    getPreviousPageParam: (firstPage) => firstPage.previousCursor,
  });
}

export function useGetFilterCategoryList() {
  return useQuery({
    queryFn: () => getFilterCategoryList(),
    queryKey: [GET_FILTER_CATEGORY_LIST],
  });
}

export function getFilterCategoryListOptions() {
  return queryOptions({
    queryFn: () => getFilterCategoryList(),
    queryKey: [GET_FILTER_CATEGORY_LIST],
  });
}

export function useGetArchiveAdsBanner(id: getAdsBannerType = '', enabled: boolean) {
  return useQuery({
    queryFn: () => getArchiveAdsBanner(id),
    queryKey: [GET_ARCHIVE_ADS_BANNER],
    enabled: enabled,
  });
}
